/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { FormFieldSchema, FormFieldState, FormFieldValue, FormSchema, FormState, ValidatorErrorMessage } from 'src/core/types/form';

/**
 * Creates a react hook for creating and updating form state. Given an initial form
 * schema, this hook outputs a form state object, an updater function for that form
 * state, and a Dto object that matches the signature of the generic type T given.
 * Note that T is used for type safety and intellisense.
 *
 * @param formSchema The form schema to base the form state on.
 */
export const useFormState = <T extends Record<string, FormFieldValue>>(formSchema: FormSchema<T>) => {
    const { localize } = useLocalization();
    const [formState, setState] = React.useState(getNextFormState(undefined, undefined, formSchema, localize));

    const updateFormState = (partial: Partial<T>): void => {
        setState(prevState => getNextFormState(prevState, partial, formSchema, localize));
    };

    const formStateDto = getFormStateDto(formState);

    const resetFormState = () => setState(getNextFormState(undefined, undefined, formSchema, localize));

    return { formState, updateFormState, formStateDto, resetFormState };
};

/**
 * Gets the next form state by processing the validity of the existing form
 * state and the new partial updates created by the user when changing the
 * form. To derive the new state, the original form schema is needed to
 * validate the new values. In the case of the first form render, the
 * previous state and partial updates are undefined, and the form schema
 * only is used to derive the new (and initial) state.
 *
 * @param prevState Previous form state before new changes occurred. This value
 * is undefined on the first render.
 * @param partial Partial updates caused by user change in form values. This
 * value is undefined on the first render.
 * @param formSchema The original form schema that contains the initial values
 * and the validators for each field.
 * @returns A new state object derived from the previous state and the new
 * partial change values.
 */
const getNextFormState = <T extends Record<string, FormFieldValue>>(
    prevState: FormState<T>,
    partial: Partial<T>,
    formSchema: FormSchema<T>,
    localize: ReactIntl.InjectedIntl['formatMessage']
): FormState<T> => {
    const keys = formSchema ? Object.keys(formSchema) : [];

    const newFields = keys.reduce((acc, key) => {
        let newVal: FormFieldValue;
        let touched: boolean;

        /**
         * The value and touched parameter can be fetched from either
         * of these cases:
         *
         * - A partial update was incurred by the a user change, check
         *   if the key exists in that partial object. If it does, then
         *   that is the new value of the key/field and should be used
         *   as the new value of the field in the new state object. The
         *   user changed the field value, therefore it has been touched.
         *   A partial can only be undefined in the case when the form
         *   is rendering for the first time.
         *
         * - If not found, then both the value and the touched parameter
         *   will be carried over from the previous state object, on the
         *   condition that the previous state object is defined. The
         *   previous state can be undefined in the case of the render
         *   (initialization).
         *
         * - In the case of the first render, the previous state is not
         *   defined, and the partial update is empty, therefor the value
         *   for the state rendering is defined by the initial value of
         *   the field as defined in the form schema object and since this
         *   is the initial render, the fields have not been touched.
         */
        if (partial && key in partial) {
            newVal = partial[key];
            touched = true;
        } else if (prevState) {
            newVal = prevState.fields[key].value;
            touched = prevState.fields[key].touched;
        } else {
            [newVal] = formSchema[key];
            touched = false;
        }

        return { ...acc, [key]: getNextFormFieldState(newVal, touched, formSchema[key], localize) };
    }, <{ [key in keyof T]: FormFieldState<T[key]> }>{});

    const isFormValid = Object.keys(newFields).every(key => newFields[key].valid);

    return {
        fields: newFields,
        form: {
            valid: isFormValid
        }
    };
};

/**
 * Gets the next state for a field in the form. Derives the new field state
 * using the new value and the current form field schema, which contains the
 * validators to fire to check the new value.
 *
 * @param newValue The value to use to create the new state.
 * @param formFieldSchema The schema of the current field. Used to evaluate the
 * new value's validity.
 * @returns A new form field state derived from the new value and the schema.
 */
const getNextFormFieldState = <T extends FormFieldValue>(
    newValue: T,
    touched: boolean,
    formFieldSchema: FormFieldSchema<typeof newValue>,
    localize: ReactIntl.InjectedIntl['formatMessage']
): FormFieldState<T> => {
    const [, ...validators] = formFieldSchema;
    let errorMessage: ValidatorErrorMessage;

    for (const validator of validators) {
        errorMessage = validator(newValue);

        if (errorMessage) {
            if (errorMessage !== undefined) {
                break;
            }
        }
    }

    return {
        value: newValue,
        valid: errorMessage === undefined,
        errorMessage: touched ? getErrorMessage(errorMessage, localize) : undefined,
        touched
    };
};

/**
 * Gets the error string message from the validator error object.
 */
const getErrorMessage = (errorMsg: ValidatorErrorMessage, localize: ReactIntl.InjectedIntl['formatMessage']): string => {
    if (!errorMsg) {
        return;
    }

    if (typeof errorMsg === 'string') {
        return errorMsg;
    }
    const { messageDescriptor, params } = errorMsg;

    return localize(messageDescriptor, params);
};

/**
 * Exports the form state as a DTO object that is a slimmed down version
 * of the form state with only the keys and their values in the object.
 *
 * @param formState The current state of the form.
 * @returns A dto object of type T that has the keys and values of the state.
 */
const getFormStateDto = <T extends Record<string, FormFieldValue>>(formState: FormState<T>): T => {
    const keys = formState ? Object.keys(formState.fields) : [];

    return keys.reduce((acc, key: keyof T) => ({ ...acc, [key]: formState.fields[key].value }), <T>{});
};
